/**
 * Bidi / direction discipline (Deliverable D) — MathToolImplementation.md §6.5,
 * Architecture_V2.01.md §6.
 *
 * Stable technical ids (claim/constraint/edge ids, digests, `W1`/`M0` grades) must read
 * LTR even when embedded in RTL text, or an Arabic/Hebrew UI would visually reorder them
 * and break copy-paste. We isolate them with Unicode bidi controls — the platform shaper
 * does the rest. Phase 1 exercises this with English + a pseudo-locale; Phase 2 drops in
 * Arabic with zero code change here.
 */
/** Locales whose script is written right-to-left. Extend as catalogs are added. */
const RTL_LANGUAGES = new Set([
    'ar', // Arabic
    'he', // Hebrew
    'iw', // Hebrew (legacy code)
    'fa', // Persian
    'ur', // Urdu
    'ps', // Pashto
    'dv', // Divehi (Thaana)
    'syr', // Syriac
    'yi', // Yiddish
    'ckb', // Sorani (Central) Kurdish — Arabic script
    'sd', // Sindhi — Arabic script
    'ug', // Uyghur — Arabic script
    'nqo', // N'Ko
]);
/**
 * ISO-15924 script subtags that are written right-to-left. An explicit script subtag is
 * authoritative over the language default, so `az-Arab` is RTL while bare `az` is LTR, and
 * `ku-Latn` is LTR while `ku-Arab` (Sorani) is RTL.
 */
const RTL_SCRIPTS = new Set([
    'arab', // Arabic
    'aran', // Nastaliq (Arabic)
    'hebr', // Hebrew
    'syrc', // Syriac
    'thaa', // Thaana
    'nkoo', // N'Ko
    'mend', // Mende Kikakui
    'samr', // Samaritan
    'adlm', // Adlam
]);
/** The explicit script subtag of a locale (`zh-Hant` → `'hant'`), lower-cased, if any. */
function scriptSubtag(locale) {
    for (const part of locale.split('-').slice(1)) {
        if (/^[A-Za-z]{4}$/.test(part))
            return part.toLowerCase();
    }
    return undefined;
}
/** The primary subtag of a BCP-47 locale, lower-cased (`en-US` → `en`). */
export function primarySubtag(locale) {
    const base = locale.split('-')[0] ?? locale;
    return base.toLowerCase();
}
/**
 * Direction for a locale. An explicit script subtag wins (`az-Arab` → RTL, `ku-Latn` → LTR);
 * otherwise the primary language subtag's default applies.
 */
export function directionOf(locale) {
    const sub = scriptSubtag(locale);
    if (sub)
        return RTL_SCRIPTS.has(sub) ? 'rtl' : 'ltr';
    return RTL_LANGUAGES.has(primarySubtag(locale)) ? 'rtl' : 'ltr';
}
// --- Unicode bidi isolate controls ---
/** FIRST STRONG ISOLATE (U+2068): direction inferred from first strong char. */
export const FSI = '⁨';
/** LEFT-TO-RIGHT ISOLATE (U+2066). */
export const LRI = '⁦';
/** RIGHT-TO-LEFT ISOLATE (U+2067). */
export const RLI = '⁧';
/** POP DIRECTIONAL ISOLATE (U+2069): closes the most recent LRI/RLI/FSI. */
export const PDI = '⁩';
/**
 * Wrap a technical id so it renders LTR regardless of surrounding text direction.
 * Uses LRI…PDI (an *isolate*, not the deprecated LRE/PDF embedding) so the id neither
 * disturbs nor is disturbed by neighbouring runs. Idempotent-safe to call once per id.
 */
export function isolateLtr(text) {
    return `${LRI}${text}${PDI}`;
}
/** First-strong isolation: let the content decide direction, but keep it isolated. */
export function isolateAuto(text) {
    return `${FSI}${text}${PDI}`;
}
/** Strip any bidi isolate controls (for assertions / plain-text export). */
export function stripBidi(text) {
    return text.replace(/[⁦⁧⁨⁩]/g, '');
}
/**
 * The HTML equivalent: `<bdi dir="ltr">…</bdi>`. The DOM renderer (Agent 5/L4) uses this
 * for technical ids inside RTL text; provided here so the direction discipline lives with
 * L5. The text is HTML-escaped to keep ids inert.
 */
export function bdiLtr(text) {
    const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return `<bdi dir="ltr">${escaped}</bdi>`;
}
//# sourceMappingURL=bidi.js.map